// ============================================================================
// UPLOAD QUEUE - Persistent background upload to S3/MinIO
// ----------------------------------------------------------------------------
// IndexedDB-backed queue that decouples artifact creation (fast, UI-blocking)
// from platform upload (slow, non-blocking). Survives tab close and resumes
// on next page load.
//
// Architecture:
//   User action → compress → IndexedDB (local cache, instant display)
//                                ↓ (background)
//                          upload-queue.js
//                                ↓
//                    file-upload-service.js (presigned URL flow)
//                                ↓
//                    S3/MinIO + file_metadata (platform truth)
//
// Design decisions:
//   - 2 concurrent uploads max (Australian mobile bandwidth)
//   - Exponential backoff retry (max 5 attempts)
//   - Online/offline aware - pauses when offline, drains when online
//   - VITE_USE_BACKEND guard - all operations are no-ops when backend is off
//   - Queue stores blobId references (4 bytes), not blob data (prevents doubling)
// ============================================================================

import db from './db';
import { isFileUploadAuthError, isFileUploadDeferralError, uploadFileToS3 } from './file-upload-service';
import { getPhotoBlob, updatePhotoS3Key } from './photo-blob-store';
import { dataUrlToBlob } from '../utils/data-url-to-blob';

const USE_BACKEND = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_USE_BACKEND === 'true';
const MAX_CONCURRENT = 2;
const MAX_RETRIES = 5;
const DRAIN_INTERVAL_MS = 5000;
const BASE_BACKOFF_MS = 2000;
const DEFAULT_CONFIRMED_RETENTION_MS = 24 * 60 * 60 * 1000;
const DEFAULT_CONFIRMED_CLEANUP_LIMIT = 50;
const MAX_CONFIRMED_CLEANUP_LIMIT = 250;

let _started = false;
let _notifyDebounce: any = null;
let _notifyTask: Promise<void> | null = null;
const _listeners = new Set<any>();
const _enqueueFlightsByQueue = new WeakMap<object, Map<string, Promise<number | null>>>();

type UploadQueueWorkerSession = {
    epoch: number;
    backendOn: boolean;
    controller: AbortController;
    active: number;
    backendReachable: boolean | null;
    drainTimer: ReturnType<typeof setInterval> | null;
    reprobeTimer: ReturnType<typeof setTimeout> | null;
    probeController: AbortController | null;
    onlineHandler: (() => void) | null;
    visibilityHandler: (() => void) | null;
    drainTasks: Set<Promise<void>>;
    workerTasks: Set<Promise<void>>;
};

let _workerEpoch = 0;
let _workerSession: UploadQueueWorkerSession | null = null;
let _stopPromise: Promise<void> | null = null;

type UploadQueueAccessOptions = {
    backendOn?: boolean;
    uploadQueue?: any;
    transactionDb?: any;
    targetDb?: any;
    startDrain?: boolean;
};

type UploadQueueCleanupOptions = UploadQueueAccessOptions & {
    maxAgeMs?: number;
    limit?: number;
    now?: number;
};

function uploadQueueIdentity(projectId: any, entityType: any, entityId: any): string {
    return `${String(projectId)}\u0000${String(entityType)}\u0000${String(entityId)}`;
}

async function findUploadQueueEntries(uploadQueue: any, projectId: any, entityType: any, entityId: any): Promise<any[]> {
    return uploadQueue
        .where('projectId').equals(projectId)
        .filter((item: any) => item.entityType === entityType && item.entityId === entityId)
        .toArray();
}

function selectCanonicalUploadQueueEntry(entries: any[]): any | null {
    if (!Array.isArray(entries) || entries.length === 0) return null;
    const statusRank: Record<string, number> = {
        confirmed: 4,
        uploading: 3,
        pending: 2,
        failed: 1,
    };
    return [...entries].sort((left, right) => (
        (statusRank[String(right?.status)] || 0) - (statusRank[String(left?.status)] || 0)
        || Number(right?.id || 0) - Number(left?.id || 0)
    ))[0] || null;
}

/**
 * Resolve queued bytes through the same project-ownership boundary used by
 * every direct photo-blob read. A numeric IndexedDB id is not globally durable
 * identity: local database resets can reuse it for another Project.
 */
export async function resolveQueuedUploadData(item: any): Promise<any> {
    if (item?.blobId) {
        const data = await getPhotoBlob(item.blobId, item.projectId);
        if (!data) throw new Error(`Blob #${item.blobId} not found in photo_blobs`);
        return data;
    }
    if (item?.blob) return item.blob;
    throw new Error('No blob data available');
}

// ─── Public API ─────────────────────────────────────────────────────────────

/** Resolve the single canonical queue owner for a project-scoped entity. */
export async function getUploadQueueEntry(
    projectId: any,
    entityType: any,
    entityId: any,
    { backendOn = USE_BACKEND, uploadQueue = (db as any).upload_queue }: UploadQueueAccessOptions = {},
): Promise<any | null> {
    if (!backendOn || !projectId || !entityType || !entityId) return null;
    const entries = await findUploadQueueEntries(uploadQueue, projectId, entityType, entityId);
    return selectCanonicalUploadQueueEntry(entries);
}

function createUploadClaimToken(): string {
    try {
        if (typeof globalThis.crypto?.randomUUID === 'function') return globalThis.crypto.randomUUID();
    } catch { /* fall through to a process-local uniqueness token */ }
    return `upload-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Atomically create-or-claim one queue identity for a foreground upload.
 * Queue workers use the same `pending -> uploading` state boundary, so a
 * foreground save and a background drain can never both own the network call.
 */
export async function acquireUploadQueueClaim(
    { projectId, entityType, entityId, blob, blobId, contentType, originalFilename, cloudMedia }: any,
    {
        backendOn = USE_BACKEND,
        targetDb = db as any,
        uploadQueue = targetDb?.upload_queue || (db as any).upload_queue,
    }: UploadQueueAccessOptions = {},
): Promise<{ state: 'claimed' | 'waiting' | 'confirmed' | 'disabled'; item: any | null }> {
    if (!backendOn) return { state: 'disabled', item: null };
    if (!projectId || !entityType || !entityId) {
        throw new Error('Upload queue claim requires projectId, entityType, and entityId');
    }
    if (!blobId && !(blob instanceof Blob)) {
        throw new Error('Upload queue claim requires blob or blobId');
    }

    const claim = async () => {
        const entries = await findUploadQueueEntries(uploadQueue, projectId, entityType, entityId);
        const canonical = selectCanonicalUploadQueueEntry(entries);
        const duplicateIds = entries
            .filter(entry => entry?.id != null && entry.id !== canonical?.id)
            .map(entry => entry.id);
        if (duplicateIds.length > 0) {
            if (typeof uploadQueue.bulkDelete === 'function') await uploadQueue.bulkDelete(duplicateIds);
            else await Promise.all(duplicateIds.map(id => uploadQueue.delete(id)));
        }

        if (canonical?.status === 'confirmed') return { state: 'confirmed' as const, item: canonical };
        if (canonical?.status === 'uploading') return { state: 'waiting' as const, item: canonical };
        if (canonical?.status === 'pending' && !isUploadQueueItemReady(canonical, Date.now())) {
            return { state: 'waiting' as const, item: canonical };
        }

        const claimToken = createUploadClaimToken();
        const lastAttemptAt = new Date().toISOString();
        const sourcePatch: any = {
            contentType: contentType || canonical?.contentType || 'application/octet-stream',
            originalFilename: originalFilename || canonical?.originalFilename || entityId,
            ...(cloudMedia ? { cloudMedia } : {}),
            ...(blobId ? { blobId } : {}),
            ...(blob instanceof Blob ? { blob } : {}),
            status: 'uploading',
            // A fresh foreground save is an explicit new attempt with newly
            // supplied bytes. Do not inherit a historical row's exhausted
            // retry budget; pending retries retain their existing count.
            ...(canonical?.status === 'failed' ? { retryCount: 0 } : {}),
            claimToken,
            claimOwner: 'foreground',
            lastAttemptAt,
            nextAttemptAt: null,
            error: null,
        };

        if (canonical) {
            await uploadQueue.update(canonical.id, sourcePatch);
            return {
                state: 'claimed' as const,
                item: { ...canonical, ...sourcePatch },
            };
        }

        const record: any = {
            projectId,
            entityType,
            entityId,
            ...sourcePatch,
            retryCount: 0,
            createdAt: new Date().toISOString(),
            s3Key: null,
        };
        const id = await uploadQueue.add(record);
        return { state: 'claimed' as const, item: { ...record, id } };
    };

    if (typeof targetDb?.transaction === 'function') {
        return targetDb.transaction('rw', uploadQueue, claim);
    }
    return claim();
}

type UploadQueueClaimMutationOptions = {
    targetDb?: any;
    uploadQueue?: any;
    backendOn?: boolean;
    startDrain?: boolean;
    mutationGuard?: (() => boolean) | null;
};

async function mutateOwnedUploadQueueClaim(
    claim: any,
    mutation: (current: any, uploadQueue: any) => Promise<any>,
    {
        targetDb = db as any,
        uploadQueue = targetDb?.upload_queue || (db as any).upload_queue,
        mutationGuard = null,
    }: UploadQueueClaimMutationOptions = {},
): Promise<any | null> {
    if (!claim?.id || !claim?.claimToken) return null;
    const run = async () => {
        if (mutationGuard && !mutationGuard()) return null;
        const current = await uploadQueue.get(claim.id);
        if (!current
            || current.status !== 'uploading'
            || current.claimToken !== claim.claimToken) return null;
        if (mutationGuard && !mutationGuard()) return null;
        return mutation(current, uploadQueue);
    };
    if (typeof targetDb?.transaction === 'function') {
        return targetDb.transaction('rw', uploadQueue, run);
    }
    return run();
}

/** Confirm a network receipt only while the caller still owns this claim. */
export async function confirmUploadQueueClaim(
    claim: any,
    result: any,
    options: UploadQueueClaimMutationOptions = {},
): Promise<any | null> {
    return mutateOwnedUploadQueueClaim(claim, async (current, uploadQueue) => {
        const patch: any = {
            status: 'confirmed',
            s3Key: result?.s3Key || current.s3Key || null,
            fileMetadataId: result?.fileMetadataId || current.fileMetadataId || null,
            contentType: result?.contentType || current.contentType,
            confirmedAt: result?.confirmedAt || current.confirmedAt || new Date().toISOString(),
            nextAttemptAt: null,
            error: null,
            claimToken: null,
            claimOwner: null,
        };
        const hasDurableRemoteReference = Boolean(patch.s3Key || patch.fileMetadataId);
        if (hasDurableRemoteReference) {
            // A confirmed queue row remains the durable receipt adopted by
            // later saves, but the source bytes are no longer needed once the
            // backend has returned an addressable remote object.
            patch.blob = undefined;
            patch.blobId = undefined;
        }
        await uploadQueue.update(current.id, patch);
        const confirmed = { ...current, ...patch };
        if (hasDurableRemoteReference) {
            delete confirmed.blob;
            delete confirmed.blobId;
        }
        return confirmed;
    }, options);
}

/** Return a failed owned attempt to the persistent queue without minting a second owner. */
export async function deferUploadQueueClaim(
    claim: any,
    {
        error = null,
        retryAfterMs = 0,
        retryCount = claim?.retryCount || 0,
        status = 'pending',
    }: any = {},
    {
        backendOn = USE_BACKEND,
        startDrain = true,
        ...options
    }: UploadQueueClaimMutationOptions = {},
): Promise<any | null> {
    const retryDelayMs = Number.isFinite(Number(retryAfterMs)) && Number(retryAfterMs) > 0
        ? Math.max(1000, Number(retryAfterMs))
        : 0;
    const updated = await mutateOwnedUploadQueueClaim(claim, async (current, uploadQueue) => {
        const patch = {
            status,
            retryCount,
            nextAttemptAt: retryDelayMs > 0
                ? new Date(Date.now() + retryDelayMs).toISOString()
                : null,
            error: error?.message || String(error || ''),
            claimToken: null,
            claimOwner: null,
        };
        await uploadQueue.update(current.id, patch);
        return { ...current, ...patch };
    }, options);
    if (updated && status === 'pending' && startDrain) _startDrainLoop(backendOn);
    return updated;
}

/** Drop an owned foreground reservation when the failure is not queue-retryable. */
export async function releaseUploadQueueClaim(
    claim: any,
    options: UploadQueueClaimMutationOptions = {},
): Promise<boolean> {
    const released = await mutateOwnedUploadQueueClaim(claim, async (current, uploadQueue) => {
        await uploadQueue.delete(current.id);
        return true;
    }, options);
    return released === true;
}

/**
 * Enqueue a file for background upload to S3.
 * Returns immediately - upload happens asynchronously.
 *
 * @param {Object} opts
 * @param {string} opts.projectId - Parent project ID
 * @param {string} opts.entityType - 'project_photo', 'quote_pdf', etc.
 * @param {string} opts.entityId - File ID within parent entity
 * @param {Blob} [opts.blob] - File data (for PDFs and non-photo files)
 * @param {number} [opts.blobId] - Reference to photo_blobs table (for photos)
 * @param {string} opts.contentType - MIME type
 * @param {string} [opts.originalFilename] - User-facing filename
 * @param {Object} [opts.cloudMedia] - Derivative receipt for customer media
 * @param {number} [opts.retryAfterMs] - Persisted cooldown before first drain
 * @returns {Promise<number|null>} Queue item ID, or null if backend is off
 */
export async function enqueueUpload(
    { projectId, entityType, entityId, blob, blobId, contentType, originalFilename, cloudMedia, retryAfterMs }: any,
    {
        backendOn = USE_BACKEND,
        uploadQueue = (db as any).upload_queue,
        transactionDb = null,
        startDrain = true,
    }: UploadQueueAccessOptions = {},
) {
    if (!backendOn) return null;
    if (!projectId || !entityType || !entityId) {
        console.warn('[UploadQueue] Missing required fields, skipping enqueue');
        return null;
    }

    // Store blob inline for non-photo files (PDFs, documents)
    // For photos, store only the blobId reference
    const retryDelayMs = Number.isFinite(Number(retryAfterMs)) && Number(retryAfterMs) > 0
        ? Math.max(1000, Number(retryAfterMs))
        : 0;
    const record: any = {
        projectId,
        entityType,
        entityId,
        contentType: contentType || 'application/octet-stream',
        originalFilename: originalFilename || entityId,
        ...(cloudMedia ? { cloudMedia } : {}),
        status: 'pending',
        retryCount: 0,
        createdAt: new Date().toISOString(),
        lastAttemptAt: null,
        nextAttemptAt: retryDelayMs > 0
            ? new Date(Date.now() + retryDelayMs).toISOString()
            : null,
        s3Key: null,
        error: null,
    };

    const durableBlob = typeof blob === 'string' && blob.startsWith('data:')
        ? await dataUrlToBlob(blob, contentType || 'application/octet-stream')
        : blob;
    if (blobId) {
        record.blobId = blobId;
    } else if (durableBlob instanceof Blob) {
        record.blob = durableBlob;
    } else {
        console.warn('[UploadQueue] No blob or blobId provided, skipping');
        return null;
    }

    const identity = uploadQueueIdentity(projectId, entityType, entityId);
    let queueFlights = _enqueueFlightsByQueue.get(uploadQueue);
    if (!queueFlights) {
        queueFlights = new Map();
        _enqueueFlightsByQueue.set(uploadQueue, queueFlights);
    }
    const existingFlight = queueFlights.get(identity);
    if (existingFlight) return existingFlight;

    const persist = async (): Promise<number | null> => {
        const existing = selectCanonicalUploadQueueEntry(
            await findUploadQueueEntries(uploadQueue, projectId, entityType, entityId),
        );
        if (existing) {
            const patch: any = {
                contentType: record.contentType,
                originalFilename: record.originalFilename,
                ...(record.cloudMedia ? { cloudMedia: record.cloudMedia } : {}),
                ...(record.blobId ? { blobId: record.blobId } : {}),
                ...(record.blob instanceof Blob ? { blob: record.blob } : {}),
            };
            if (existing.status === 'failed') {
                Object.assign(patch, {
                    status: 'pending',
                    retryCount: 0,
                    lastAttemptAt: null,
                    nextAttemptAt: record.nextAttemptAt,
                    error: null,
                });
            }
            await uploadQueue.update(existing.id, patch);
            return existing.id;
        }

        const id = await uploadQueue.add(record);
        console.log(`[UploadQueue] Enqueued ${entityType}/${entityId} (queue #${id})`);
        return id;
    };

    const run = (async () => {
        const queueDb = transactionDb
            || (uploadQueue === (db as any).upload_queue ? (db as any) : null);
        if (typeof queueDb?.transaction === 'function') {
            return queueDb.transaction('rw', uploadQueue, persist);
        }
        return persist();
    })();
    queueFlights.set(identity, run);

    let id: number | null;
    try {
        id = await run;
    } finally {
        if (queueFlights.get(identity) === run) queueFlights.delete(identity);
    }

    // Start the drain loop on first enqueue (lazy - never during file import)
    if (startDrain) _startDrainLoop(backendOn);
    _notifyDebounced();

    return id;
}

/**
 * Get queue status counts.
 */
export async function getQueueStatus() {
    if (!USE_BACKEND) return { pending: 0, uploading: 0, confirmed: 0, failed: 0, total: 0 };

    const all = await (db as any).upload_queue.toArray();
    const counts = { pending: 0, uploading: 0, confirmed: 0, failed: 0, total: all.length };
    for (const item of all) {
        if ((counts as any)[item.status] !== undefined) (counts as any)[item.status]++;
    }
    return counts;
}

/**
 * Retry all failed uploads (optionally filtered by project).
 */
export async function retryFailed(projectId?: any) {
    if (!USE_BACKEND) return;

    const query = projectId
        ? (db as any).upload_queue.where('[projectId+status]').equals([projectId, 'failed'])
        : (db as any).upload_queue.where('status').equals('failed');

    await query.modify({ status: 'pending', retryCount: 0, nextAttemptAt: null, error: null });
    _notifyDebounced();
    if (_workerSession) _scheduleDrain(_workerSession);
    else _startDrainLoop(USE_BACKEND);
}

/**
 * Remove every persisted upload-queue row owned by one Project.
 *
 * Project deletion is a stronger boundary than queue status: pending,
 * uploading, failed, and confirmed receipts all belong to the deleted
 * Project. The write transaction serializes with queue claims so a worker
 * cannot retain or newly claim an orphaned row during the removal.
 */
export async function deleteQueuedUploadsForProject(
    projectId: any,
    {
        targetDb = db as any,
        uploadQueue = targetDb?.upload_queue || (db as any).upload_queue,
    }: UploadQueueAccessOptions = {},
): Promise<number> {
    if (!projectId || !uploadQueue) return 0;

    const remove = async () => Number(
        await uploadQueue.where('projectId').equals(projectId).delete(),
    ) || 0;
    const deleted = typeof targetDb?.transaction === 'function'
        ? await targetDb.transaction('rw', uploadQueue, remove)
        : await remove();

    if (deleted > 0 && uploadQueue === (db as any).upload_queue) _notifyDebounced();
    return deleted;
}

/**
 * Subscribe to queue progress updates.
 * @param {Function} callback - Called with { pending, uploading, confirmed, failed, total }
 * @returns {Function} Unsubscribe function
 */
export function onQueueProgress(callback: any) {
    _listeners.add(callback);
    return () => _listeners.delete(callback);
}

/**
 * S3 (2026-08-19): resume the drain loop after a tab reload. The queue is
 * crash-safe by storage but the loop only ever started lazily on the first
 * enqueueUpload() of a session — so rows that survived a reload sat pending
 * forever unless something new was enqueued. Called once at app boot; starts
 * the loop ONLY when there is actual work, preserving the no-auto-start
 * rationale below (an empty queue must not spend a health probe).
 *
 * @returns {Promise<boolean>} true when the loop was started for surviving work
 */
export async function resumeUploadQueue(
    // Injectable for tests ONLY: vi.stubEnv does not reach the SSR-transformed
    // module's import.meta.env in this repo's vitest setup (verified 2026-08-19
    // by printing the transformed source: the read is runtime, the stub never
    // lands). Production callers pass nothing and get the real env gate.
    { backendOn }: { backendOn?: boolean } = {},
): Promise<boolean> {
    let on = backendOn;
    if (on === undefined) {
        try {
            on = (import.meta as { env?: Record<string, string | undefined> } | undefined)?.env?.VITE_USE_BACKEND === 'true';
        } catch { on = false; }
    }
    if (!on || _started) return false;
    try {
        const survivors = await (db as any).upload_queue
            .where('status').anyOf('pending', 'uploading')
            .count();
        if (survivors > 0) {
            console.info(`[UploadQueue] Resuming ${survivors} upload(s) that survived the last session.`);
            _startDrainLoop(on);
            return true;
        }
    } catch (err: any) {
        console.warn('[UploadQueue] Resume check failed:', err?.message);
    }
    return false;
}

/**
 * Delete a bounded batch of old, safely confirmed upload receipts.
 *
 * Active and retryable rows are never selected. A remote key/metadata receipt
 * is also required: a historical row marked `confirmed` without an
 * addressable remote object may still hold the customer's only local bytes.
 */
export async function cleanupStaleConfirmedUploads({
    maxAgeMs = DEFAULT_CONFIRMED_RETENTION_MS,
    limit = DEFAULT_CONFIRMED_CLEANUP_LIMIT,
    now = Date.now(),
    backendOn = USE_BACKEND,
    targetDb = db as any,
    uploadQueue = targetDb?.upload_queue || (db as any).upload_queue,
}: UploadQueueCleanupOptions = {}): Promise<number> {
    if (!backendOn || !uploadQueue) return 0;

    const requestedLimit = Number(limit);
    const boundedLimit = Number.isFinite(requestedLimit)
        ? Math.min(MAX_CONFIRMED_CLEANUP_LIMIT, Math.max(0, Math.floor(requestedLimit)))
        : DEFAULT_CONFIRMED_CLEANUP_LIMIT;
    const requestedAge = Number(maxAgeMs);
    const currentTime = Number(now);
    if (boundedLimit === 0
        || !Number.isFinite(requestedAge)
        || requestedAge < 0
        || !Number.isFinite(currentTime)) return 0;

    const cutoffMs = currentTime - requestedAge;
    const remove = async () => {
        const stale = await uploadQueue
            .where('status').equals('confirmed')
            .filter((item: any) => {
                if (!item?.s3Key && !item?.fileMetadataId) return false;
                const receiptAt = new Date(item.confirmedAt || item.createdAt || '').getTime();
                return Number.isFinite(receiptAt) && receiptAt < cutoffMs;
            })
            .limit(boundedLimit)
            .toArray();
        const ids = stale
            .map((item: any) => item?.id)
            .filter((id: any) => id != null);
        if (ids.length === 0) return 0;
        if (typeof uploadQueue.bulkDelete === 'function') await uploadQueue.bulkDelete(ids);
        else await Promise.all(ids.map((id: any) => uploadQueue.delete(id)));
        return ids.length;
    };

    const deleted = typeof targetDb?.transaction === 'function'
        ? await targetDb.transaction('rw', uploadQueue, remove)
        : await remove();
    if (deleted > 0 && uploadQueue === (db as any).upload_queue) _notifyDebounced();
    return deleted;
}

/**
 * Backwards-compatible 24-hour cleanup entrypoint. Cleanup is intentionally
 * bounded; repeated maintenance ticks can drain additional stale receipts.
 */
export async function cleanupConfirmed(maxAgeMs = DEFAULT_CONFIRMED_RETENTION_MS): Promise<number> {
    return cleanupStaleConfirmedUploads({ maxAgeMs });
}

// ─── Drain Loop ─────────────────────────────────────────────────────────────

// S3 (2026-08-19): `backendOn` parameter so resumeUploadQueue can thread its
// resolved gate through — the module-level USE_BACKEND is captured at load
// time and the two must not disagree about whether the loop really started.
// Every production caller passes nothing and gets the original behaviour.
function _startDrainLoop(backendOn = USE_BACKEND) {
    if (_started || !backendOn) return;
    if (_stopPromise) {
        void _stopPromise.then(() => _startDrainLoop(backendOn));
        return;
    }
    _started = true;

    const session: UploadQueueWorkerSession = {
        epoch: ++_workerEpoch,
        backendOn,
        controller: new AbortController(),
        active: 0,
        backendReachable: null,
        drainTimer: null,
        reprobeTimer: null,
        probeController: null,
        onlineHandler: null,
        visibilityHandler: null,
        drainTasks: new Set(),
        workerTasks: new Set(),
    };
    _workerSession = session;

    // Drain on interval
    session.drainTimer = setInterval(() => _scheduleDrain(session), DRAIN_INTERVAL_MS);

    // Drain immediately when coming online
    if (typeof window !== 'undefined') {
        session.onlineHandler = () => _scheduleDrain(session);
        session.visibilityHandler = () => {
            if (!document.hidden) _scheduleDrain(session);
        };
        window.addEventListener('online', session.onlineHandler);
        document.addEventListener('visibilitychange', session.visibilityHandler);
    }

    // Initial drain
    _scheduleDrain(session);
    console.log('[UploadQueue] Drain loop started');
}

function _isCurrentWorkerSession(session: UploadQueueWorkerSession): boolean {
    return _workerSession === session
        && _started
        && _workerEpoch === session.epoch
        && !session.controller.signal.aborted;
}

function _scheduleDrain(session: UploadQueueWorkerSession): void {
    if (!_isCurrentWorkerSession(session)) return;
    const task = _drainOnce(session);
    session.drainTasks.add(task);
    void task.then(
        () => session.drainTasks.delete(task),
        () => session.drainTasks.delete(task),
    );
}

function _scheduleWorker(session: UploadQueueWorkerSession, item: any): void {
    if (!_isCurrentWorkerSession(session)) return;
    const task = _processItem(session, item);
    session.workerTasks.add(task);
    void task.then(
        () => session.workerTasks.delete(task),
        () => session.workerTasks.delete(task),
    );
}

/**
 * Stop this tab's background upload worker before its tenant database closes.
 *
 * The returned promise resolves only after every drain and upload task owned by
 * the stopped worker has crossed its final mutation fence. Callers that close
 * or switch the local tenant database must await it. Repeated calls share the
 * same stop promise and are safe.
 */
export function stopUploadQueue(): Promise<void> {
    if (_stopPromise) return _stopPromise;

    const session = _workerSession;
    if (!session) {
        if (_notifyDebounce) {
            clearTimeout(_notifyDebounce);
            _notifyDebounce = null;
        }
        _listeners.clear();
        return Promise.resolve();
    }

    // Invalidate the mutation fence before aborting network work. A request
    // that settles synchronously from abort must see the worker as stale.
    _workerSession = null;
    _started = false;
    _workerEpoch += 1;

    if (session.drainTimer) clearInterval(session.drainTimer);
    session.drainTimer = null;
    if (session.reprobeTimer) clearTimeout(session.reprobeTimer);
    session.reprobeTimer = null;
    if (_notifyDebounce) clearTimeout(_notifyDebounce);
    _notifyDebounce = null;

    if (typeof window !== 'undefined') {
        if (session.onlineHandler) window.removeEventListener('online', session.onlineHandler);
        if (session.visibilityHandler) document.removeEventListener('visibilitychange', session.visibilityHandler);
    }
    session.onlineHandler = null;
    session.visibilityHandler = null;
    _listeners.clear();

    session.controller.abort('QuoteMaker upload queue stopped');
    session.probeController?.abort('QuoteMaker upload queue stopped');

    let stopTask: Promise<void>;
    stopTask = (async () => {
        const tasks = [
            ...session.drainTasks,
            ...session.workerTasks,
            ...(_notifyTask ? [_notifyTask] : []),
        ];
        await Promise.allSettled(tasks);
    })().finally(() => {
        if (_stopPromise === stopTask) _stopPromise = null;
    });
    _stopPromise = stopTask;
    return stopTask;
}

function isUploadQueueItemReady(item: any, now: number): boolean {
    if (item.nextAttemptAt) {
        const nextAttemptAt = new Date(item.nextAttemptAt).getTime();
        if (Number.isFinite(nextAttemptAt) && now < nextAttemptAt) return false;
    }
    if (!item.lastAttemptAt) return true;
    const backoff = BASE_BACKOFF_MS * Math.pow(2, Math.min(item.retryCount || 0, 4));
    return now - new Date(item.lastAttemptAt).getTime() >= backoff;
}

/**
 * Atomically claim one pending row before touching its bytes or the network.
 * IndexedDB read/write transactions serialize across tabs for the same object
 * store, so only one queue worker can turn a row from pending to uploading.
 */
export async function claimPendingUploadForProcessing({
    targetDb = db as any,
    itemId,
    now = Date.now(),
    claimGuard = null,
}: {
    targetDb?: any;
    itemId: any;
    now?: number;
    claimGuard?: (() => boolean) | null;
}): Promise<any | null> {
    if (!itemId || !targetDb?.upload_queue) return null;
    const uploadQueue = targetDb.upload_queue;
    const claim = async () => {
        if (claimGuard && !claimGuard()) return null;
        const current = await uploadQueue.get(itemId);
        if (!current || current.status !== 'pending' || !isUploadQueueItemReady(current, now)) {
            return null;
        }
        if (claimGuard && !claimGuard()) return null;
        const identityEntries = await findUploadQueueEntries(
            uploadQueue,
            current.projectId,
            current.entityType,
            current.entityId,
        );
        if (claimGuard && !claimGuard()) return null;
        const canonical = selectCanonicalUploadQueueEntry(identityEntries);
        const duplicateIds = identityEntries
            .filter(entry => entry?.id != null && entry.id !== canonical?.id)
            .map(entry => entry.id);
        if (duplicateIds.length > 0) {
            if (claimGuard && !claimGuard()) return null;
            if (typeof uploadQueue.bulkDelete === 'function') {
                await uploadQueue.bulkDelete(duplicateIds);
            } else {
                await Promise.all(duplicateIds.map(id => uploadQueue.delete(id)));
            }
        }
        if (!canonical || canonical.id !== current.id || canonical.status !== 'pending') {
            return null;
        }
        const lastAttemptAt = new Date(now).toISOString();
        const claimToken = createUploadClaimToken();
        const claimPatch = {
            status: 'uploading',
            lastAttemptAt,
            claimToken,
            claimOwner: 'background',
        };
        if (claimGuard && !claimGuard()) return null;
        await uploadQueue.update(itemId, claimPatch);
        return { ...current, ...claimPatch };
    };
    if (typeof targetDb.transaction === 'function') {
        return targetDb.transaction('rw', uploadQueue, claim);
    }
    return claim();
}

/**
 * Select the next drain candidates without letting early cooldown rows consume
 * every concurrency slot. Exported only to keep the fairness invariant under a
 * behavioural regression test; production passes the Dexie upload_queue table.
 */
export async function selectReadyPendingUploadsForDrain(
    uploadQueue: any,
    slots: number,
    now = Date.now(),
): Promise<any[]> {
    if (slots <= 0) return [];
    return uploadQueue
        .where('status').equals('pending')
        .filter((item: any) => isUploadQueueItemReady(item, now))
        .limit(slots)
        .toArray();
}

async function _drainOnce(session: UploadQueueWorkerSession) {
    if (!_isCurrentWorkerSession(session) || !session.backendOn) return;
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    if (session.active >= MAX_CONCURRENT) return;

    // Fast-fail if backend isn't reachable (re-probe every 30 seconds)
    if (session.backendReachable === false) return;
    if (session.backendReachable === null) {
        const ok = await _probeBackend(session);
        if (!_isCurrentWorkerSession(session)) return;
        if (!ok) {
            // Re-probe in 30 seconds
            session.reprobeTimer = setTimeout(() => {
                if (!_isCurrentWorkerSession(session)) return;
                session.backendReachable = null;
                session.reprobeTimer = null;
            }, 30000);
            return;
        }
    }

    try {
        if (!_isCurrentWorkerSession(session)) return;
        // Reset any items stuck in 'uploading' (from crashed tabs)
        await ((db as any).upload_queue.where('status').equals('uploading') as import('dexie').Collection<any>)
            .filter(item => {
                // If uploading for > 5 minutes, it's stuck
                const elapsed = Date.now() - new Date(item.lastAttemptAt || 0).getTime();
                return elapsed > 5 * 60 * 1000;
            })
            .modify({ status: 'pending' });
        if (!_isCurrentWorkerSession(session)) return;

        // Pick pending items with backoff check
        const slots = MAX_CONCURRENT - session.active;
        if (slots <= 0) return;

        // Apply cooldown/backoff eligibility before the slot limit. Otherwise
        // two early cooling rows can starve ready work from every later project.
        const ready = await selectReadyPendingUploadsForDrain(
            (db as any).upload_queue,
            slots,
        );
        if (!_isCurrentWorkerSession(session)) return;

        for (const item of ready) {
            _scheduleWorker(session, item);
        }
    } catch (err: any) {
        if (_isCurrentWorkerSession(session)) {
            console.warn('[UploadQueue] Drain error:', err?.message);
        }
    }
}

async function _processItem(session: UploadQueueWorkerSession, item: any) {
    if (!_isCurrentWorkerSession(session)) return;
    session.active++;
    _notifyDebounced();
    let claimed = false;
    const mutationGuard = () => _isCurrentWorkerSession(session);

    try {
        const claimedItem = await claimPendingUploadForProcessing({
            itemId: item.id,
            claimGuard: mutationGuard,
        });
        if (!claimedItem) return;
        claimed = true;
        item = claimedItem;
        if (!mutationGuard()) return;

        // Resolve blob data only after re-checking the Project owner. Local
        // IndexedDB ids can be reused after a reset; the queue row alone is
        // not authority to read bytes from a different Project.
        const data = await resolveQueuedUploadData(item);
        if (!mutationGuard()) return;

        // Upload to S3
        const result = await uploadFileToS3({
            data,
            projectId: item.projectId,
            entityType: item.entityType,
            entityId: item.entityId,
            contentType: item.contentType,
            originalFilename: item.originalFilename,
            cloudMedia: item.cloudMedia,
            signal: session.controller.signal,
        } as any);
        if (!mutationGuard()) return;

        // Mark confirmed only while this worker still owns the atomic claim.
        const confirmed = await confirmUploadQueueClaim(item, result, { mutationGuard });
        if (!confirmed) {
            if (!mutationGuard()) return;
            throw Object.assign(new Error('Upload queue ownership changed before confirmation'), {
                code: 'UPLOAD_QUEUE_CLAIM_LOST',
            });
        }

        // For photos, stamp the s3Key back onto the blob record. S3
        // (2026-08-19): through the GUARDED path — blob ids are global
        // auto-increments, and the raw table update this used to do could
        // annotate another project's blob row (the exact class
        // updatePhotoS3Key's ownership check exists for).
        if (mutationGuard() && item.blobId && result.s3Key) {
            try {
                await updatePhotoS3Key(item.blobId, result.s3Key, item.projectId);
            } catch { /* non-critical */ }
        }

        if (mutationGuard()) {
            console.log(`[UploadQueue] Uploaded ${item.entityType}/${item.entityId} → ${result.s3Key}`);
        }
    } catch (err: any) {
        if (!mutationGuard()) return;
        if (!claimed) {
            console.warn(`[UploadQueue] Could not claim queue #${item?.id}:`, err?.message);
            return;
        }
        if (isFileUploadDeferralError(err)) {
            const retryAfterMs = Math.max(1000, err.retryAfterMs || (err.retryAfterSeconds || 1) * 1000);
            await deferUploadQueueClaim(item, {
                error: err,
                retryAfterMs,
            }, { mutationGuard, startDrain: false });
            if (!mutationGuard()) return;
            console.warn(`[UploadQueue] Deferred ${item.entityType}/${item.entityId} for ${Math.ceil(retryAfterMs / 1000)}s after backend rate limiting.`);
            return;
        }

        if (isFileUploadAuthError(err)) {
            const retryAfterMs = 60000;
            await deferUploadQueueClaim(item, {
                error: err,
                retryAfterMs,
            }, { mutationGuard, startDrain: false });
            if (!mutationGuard()) return;
            console.info(`[UploadQueue] Paused ${item.entityType}/${item.entityId} until the QuoteMaker login session is refreshed.`);
            return;
        }

        const newRetry = (item.retryCount || 0) + 1;
        const newStatus = newRetry >= MAX_RETRIES ? 'failed' : 'pending';

        await deferUploadQueueClaim(item, {
            error: err,
            retryCount: newRetry,
            status: newStatus,
        }, { mutationGuard, startDrain: false });

        if (!mutationGuard()) return;
        if (newStatus === 'failed') {
            console.error(`[UploadQueue] FAILED (max retries) ${item.entityType}/${item.entityId}:`, err?.message);
        } else {
            console.warn(`[UploadQueue] Retry ${newRetry}/${MAX_RETRIES} for ${item.entityType}/${item.entityId}:`, err?.message);
        }
    } finally {
        session.active = Math.max(0, session.active - 1);
        if (_isCurrentWorkerSession(session)) _notifyDebounced();
    }
}

function _notifyDebounced() {
    if (_notifyDebounce) return; // Already scheduled
    _notifyDebounce = setTimeout(() => {
        _notifyDebounce = null;
        if (_listeners.size === 0) return;
        const task = (async () => {
            try {
                const status = await getQueueStatus();
                for (const cb of _listeners) {
                    try { cb(status); } catch { /* listener error, ignore */ }
                }
            } catch { /* status fetch failed, ignore */ }
        })();
        _notifyTask = task;
        void task.finally(() => {
            if (_notifyTask === task) _notifyTask = null;
        });
    }, 1000); // Debounce: max 1 notification per second
}

// Fast-fail probe: check if backend is reachable before attempting uploads.
// Each worker session owns its probe signal and timeout, so tenant teardown can
// abort the request and await the drain that called it.
async function _probeBackend(session: UploadQueueWorkerSession): Promise<boolean> {
    if (!_isCurrentWorkerSession(session)) return false;
    try {
        const ctrl = new AbortController();
        session.probeController = ctrl;
        const timer = setTimeout(() => ctrl.abort('QuoteMaker backend health probe timed out'), 3000);
        try {
            const resp = await fetch('/api/health', { signal: ctrl.signal });
            if (_isCurrentWorkerSession(session)) session.backendReachable = resp.ok;
        } finally {
            clearTimeout(timer);
            if (session.probeController === ctrl) session.probeController = null;
        }
    } catch {
        if (_isCurrentWorkerSession(session)) session.backendReachable = false;
    }
    return _isCurrentWorkerSession(session) ? session.backendReachable === true : false;
}

// ─── NO auto-start ──────────────────────────────────────────────────────────
// The drain loop is NOT started on module load. It starts lazily on the first
// enqueueUpload() call. This prevents the backend health probe from consuming
// browser connection slots during file processing (Windows: proxy connections
// to dead ports can hang for 20+ seconds, starving AI API calls).
